import { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "@/shared/hooks/use-toast";

import {
  mapLeadService,
  toConvertRequest,
  type MapLeadAnalysis,
  type MapLeadItem,
  type MapLeadQuota,
  type MapLeadSearchRequest,
} from "../api/mapLeadService";
import { errorToast } from "@/shared/lib/apiErrors";
import { getPersona } from "@/shared/brand/persona";

export interface SearchForm {
  city: string;
  district: string;
  industry: string;
  keyword: string;
  mode: "KEYWORD" | "AROUND";
  /** 主推产品 nodeCode(选填):评分与军师分析都围绕它;空串=未选。 */
  productNodeCode: string;
  /**
   * 主推产品自由输入(选填,2026-09-07):没建产品库、或想自定义输入产品名时用它;
   * 空串=未填。UI 保证与 productNodeCode 互斥(选中真实产品会清空这里,反之亦然)。
   */
  productText: string;
  radiusKm: number;
  onlyWithPhone: boolean;
  onlyHighScore: boolean;
}

export const DEFAULT_FORM: SearchForm = {
  city: "上海市",
  district: "",
  industry: "",
  keyword: "",
  mode: "KEYWORD",
  productNodeCode: "",
  productText: "",
  radiusKm: 5,
  onlyWithPhone: false,
  onlyHighScore: false,
};

/** 地图获客「找客户」Tab 的全部状态编排(搜索/配额/选中/加线索)。 */
export function useMapLead() {
  const [form, setForm] = useState<SearchForm>(DEFAULT_FORM);
  const [center, setCenter] = useState<{ lng: number; lat: number } | null>(null);
  const [items, setItems] = useState<MapLeadItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [quota, setQuota] = useState<MapLeadQuota | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [joinedPoiIds, setJoinedPoiIds] = useState<Set<string>>(new Set());
  // poiId → leadId(convert 回执),军师分析要挂在 leadId 上。
  const [leadIds, setLeadIds] = useState<Map<string, number>>(new Map());
  const [analyses, setAnalyses] = useState<Map<string, MapLeadAnalysis>>(new Map());
  const [analyzingPoiId, setAnalyzingPoiId] = useState<string | null>(null);

  useEffect(() => {
    mapLeadService
      .quota()
      .then(setQuota)
      .catch(() => undefined); // 配额只是展示,拿不到不拦功能
  }, []);

  const patch = useCallback((p: Partial<SearchForm>) => {
    setForm((f) => ({ ...f, ...p }));
  }, []);

  const runSearch = useCallback(
    async (targetPage: number) => {
      const req: MapLeadSearchRequest = {
        city: form.city.trim() || undefined,
        district: form.district.trim() || undefined,
        keyword: form.keyword.trim() || undefined,
        industry: form.industry.trim() || undefined,
        searchMode: form.mode,
        productNodeCode: form.productNodeCode || undefined,
        productText: form.productText.trim() || undefined,
        page: targetPage,
        pageSize: 20,
      };
      if (form.mode === "AROUND") {
        if (!center) {
          toast({ title: "请先在地图上点选中心点", variant: "destructive" });
          return;
        }
        req.longitude = center.lng;
        req.latitude = center.lat;
        req.radius = form.radiusKm * 1000;
      } else if (!req.keyword && !req.industry) {
        toast({ title: "请输入关键词或选择客户类型", variant: "destructive" });
        return;
      }
      setLoading(true);
      try {
        const resp = await mapLeadService.search(req);
        setItems(resp.items);
        setHasMore(resp.hasMore);
        setPage(resp.page);
        setQuota(resp.quota);
        setSearched(true);
        setSelectedPoiId(resp.items[0]?.poiId ?? null);
        setChecked(new Set());
      } catch (e) {
        toast({
          title: "搜索失败",
          description: errorToast(e).description,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [form, center]
  );

  const search = useCallback(() => runSearch(1), [runSearch]);
  const gotoPage = useCallback((p: number) => runSearch(p), [runSearch]);

  /** 客户端展示过滤(更多筛选;不打后端)。 */
  const visibleItems = useMemo(() => {
    return items.filter((i) => {
      if (form.onlyWithPhone && !i.phone) return false;
      if (form.onlyHighScore && i.score < 70) return false;
      return true;
    });
  }, [items, form.onlyWithPhone, form.onlyHighScore]);

  const selected = useMemo(
    () => items.find((i) => i.poiId === selectedPoiId) ?? null,
    [items, selectedPoiId]
  );

  const convertOne = useCallback(async (item: MapLeadItem) => {
    try {
      const r = await mapLeadService.convert(toConvertRequest(item));
      setJoinedPoiIds((s) => new Set(s).add(item.poiId));
      setLeadIds((m) => new Map(m).set(item.poiId, r.leadId));
      toast({
        title: r.duplicated ? "已在线索池" : "已加入线索池",
        description: item.name,
      });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "加入线索失败" }));
    }
  }, []);

  const toggleChecked = useCallback((poiId: string) => {
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(poiId)) next.delete(poiId);
      else next.add(poiId);
      return next;
    });
  }, []);

  const convertChecked = useCallback(async () => {
    const targets = items.filter((i) => checked.has(i.poiId));
    if (targets.length === 0) return;
    if (targets.length > 20) {
      toast({ title: "单次批量加入最多 20 条", variant: "destructive" });
      return;
    }
    try {
      const rs = await mapLeadService.batchConvert(targets.map(toConvertRequest));
      setJoinedPoiIds((s) => {
        const next = new Set(s);
        targets.forEach((t) => next.add(t.poiId));
        return next;
      });
      setLeadIds((m) => {
        const next = new Map(m);
        rs.forEach((r, i) => {
          const t = targets[i];
          if (t) next.set(t.poiId, r.leadId);
        });
        return next;
      });
      const fresh = rs.filter((r) => !r.duplicated).length;
      toast({
        title: `已加入 ${fresh} 条线索`,
        description: rs.length - fresh > 0 ? `${rs.length - fresh} 条此前已在线索池` : undefined,
      });
      setChecked(new Set());
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "批量加入失败" }));
    }
  }, [items, checked]);

  /** 让军师分析:未入池先幂等加入拿 leadId,再按次计费分析;失败(含余额不足)不扣、可重试。 */
  const analyzeOne = useCallback(
    async (item: MapLeadItem) => {
      if (analyzingPoiId) return;
      setAnalyzingPoiId(item.poiId);
      try {
        let leadId = leadIds.get(item.poiId);
        if (leadId == null) {
          const r = await mapLeadService.convert(toConvertRequest(item));
          leadId = r.leadId;
          setJoinedPoiIds((s) => new Set(s).add(item.poiId));
          setLeadIds((m) => new Map(m).set(item.poiId, r.leadId));
        }
        const analysis = await mapLeadService.analyzeLead(
          leadId,
          form.productNodeCode || undefined,
          form.productText.trim() || undefined
        );
        setAnalyses((m) => new Map(m).set(item.poiId, analysis));
        toast({ title: `${getPersona().title}分析完成`, description: item.name });
      } catch (e) {
        toast(errorToast(e, { fallbackTitle: `${getPersona().title}分析失败(未扣积分)` }));
      } finally {
        setAnalyzingPoiId(null);
      }
    },
    [analyzingPoiId, leadIds, form.productNodeCode, form.productText]
  );

  return {
    form,
    patch,
    center,
    setCenter,
    items,
    visibleItems,
    hasMore,
    page,
    quota,
    loading,
    searched,
    selected,
    selectedPoiId,
    setSelectedPoiId,
    checked,
    toggleChecked,
    joinedPoiIds,
    analyses,
    analyzingPoiId,
    analyzeOne,
    search,
    gotoPage,
    convertOne,
    convertChecked,
  };
}
